import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { useToast } from '../App';
import OrderSyncConfig from '../components/OrderSyncConfig';
import ProductSyncConfig from '../components/ProductSyncConfig';
import QuickActions from '../components/QuickActions';
import LoadingState from '../components/LoadingState';
import { isPro } from '../Helpers';
import { useModalManager } from '../hooks/useModalManager';
import PriceModal from '../components/PriceModal/PriceModal';
import AISettings from '../components/AISettings/AISettings';

const Settings = () => {
    const { showSuccess, showError } = useToast();
    const { modalConfig, closeModal, showPremiumModal } = useModalManager();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('general');

    const [settings, setSettings] = useState({
        // General Settings
        database_mode_enabled: true,
        multivendor_enabled: false,
        auto_sync_enabled: false,
        sync_interval: 30,

        // WooCommerce Settings
        wooanalytics: false,
        incomplete_orders_tracking: false,
        cart_interactions_tracking: false,
        abandonment_delay_minutes: 5,
        cleanup_incomplete_orders_days: 30,
        bulk_edit_optimize_mode: false,
        bulk_edit_batch_size: 5000,
        bulk_edit_threshold: 1000,
        analytics_date_range: 'today',
        analytics_date_from: '',
        analytics_date_to: '',
        ai_revenue_analysis: false,

        // Sync Settings
        sync_orders: true,
        sync_products: true,
        sync_customers: false,
        batch_size: 100,

        // Notification Settings
        email_notifications: true,
        notification_email: '',
        sync_success_notifications: false,
        sync_error_notifications: true
    });

    const tabs = [
        { id: 'general', label: 'General', icon: '⚙️' },
        { id: 'quick-actions', label: 'Quick Actions', icon: '⚡' },
        { id: 'woocommerce', label: 'WooCommerce', icon: '🛒' },
        { id: 'ai', label: 'AI Integration', icon: '🤖' },
        { id: 'sync', label: 'Export Settings', icon: '🔄' },
        { id: 'notifications', label: 'Notifications', icon: '🔔' },
        { id: 'more', label: 'More', icon: '➕' }
    ];



    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const response = await api.getSettings();

            if (response.success) {
                setSettings(prev => ({
                    ...prev,
                    ...response.settings
                }));
            } else {
                throw new Error(response.message || 'Failed to load settings');
            }
        } catch (err) {
            showError(err.message);
        } finally {
            setLoading(false);
        }
    };


    const handleSettingChange = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Premium-aware setting change handler
    const handlePremiumSettingChange = (key, value) => {
        // Check if this is a premium feature and user doesn't have pro access
        const premiumFeatures = ['incomplete_orders_tracking', 'cart_interactions_tracking', 'bulk_edit_optimize_mode', 'ai_revenue_analysis'];

        if (premiumFeatures.includes(key) && !isPro()) {
            showPremiumModal();
            return;
        }

        handleSettingChange(key, value);
    };


    const saveSettings = async () => {
        try {
            setSaving(true);
            const response = await api.updateSettings(settings);

            if (response.success) {
                showSuccess('Settings saved successfully');
            } else {
                throw new Error(response.message || 'Failed to save settings');
            }
        } catch (err) {
            showError(err.message);
        } finally {
            setSaving(false);
        }
    };


    if (loading) {
        return (
            <div className="page">
                <div className="container container-xl">
                    <LoadingState message="Loading settings..." />
                </div>
            </div>
        );
    }

    return (
        <div className="page settings">
            <div className="container container-xl">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h1>Settings</h1>
                        <p>Configure modes, Google Sheets integration, and preferences</p>
                    </div>
                    <div className="page-actions">
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={loadSettings}
                            disabled={loading}
                        >
                            🔄 Refresh
                        </button>
                        <button
                            className={`btn btn-primary btn-sm ${saving ? 'btn-loading' : ''}`}
                            onClick={saveSettings}
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <div className="spinner"></div>
                                    Saving...
                                </>
                            ) : (
                                '💾 Save Settings'
                            )}
                        </button>
                    </div>
                </div>

                <div className="settings-layout">
                    {/* Tabs Navigation */}
                    <div className="settings-tabs">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
                                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                                disabled={tab.disabled}
                            >
                                <span className="tab-icon">{tab.icon}</span>
                                <span className="tab-label">{tab.label}</span>
                            </button>
                        ))}
                    </div>

                    {/* Tab Content */}
                    <div className="settings-content">
                        {/* General Settings */}
                        {activeTab === 'general' && (
                            <div className="tab-panel">
                                <div className="card">
                                    <div className="card-header">
                                        <h3>Operation Modes</h3>
                                        <p>Configure how Shop Explorer operates</p>
                                    </div>
                                    <div className="card-content">
                                        <div className="settings-group">
                                            <div className="setting-item">
                                                <div className="setting-info">
                                                    <label className="setting-label">Database Mode</label>
                                                    <p className="setting-description">
                                                        Store and manage data in WordPress database
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <label className="switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={settings.database_mode_enabled}
                                                            onChange={(e) => handleSettingChange('database_mode_enabled', e.target.checked)}
                                                            disabled
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="setting-item upcoming-feature">
                                                <div className="setting-info">
                                                    <label className="setting-label">
                                                        Multivendor Mode
                                                        <span className="upcoming-badge">Coming Soon</span>
                                                    </label>
                                                    <p className="setting-description">
                                                        Enable multivendor support with individual sheet management
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <label className="switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={settings.multivendor_enabled}
                                                            onChange={(e) => handleSettingChange('multivendor_enabled', e.target.checked)}
                                                            disabled
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="setting-item upcoming-feature">
                                                <div className="setting-info">
                                                    <label className="setting-label">
                                                        Auto Export
                                                        <span className="upcoming-badge">Coming Soon</span>
                                                    </label>
                                                    <p className="setting-description">
                                                        Automatically export data at regular intervals
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <label className="switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={settings.auto_sync_enabled}
                                                            onChange={(e) => handleSettingChange('auto_sync_enabled', e.target.checked)}
                                                            disabled
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                </div>
                                            </div>

                                            {settings.auto_sync_enabled && (
                                                <div className="setting-item">
                                                    <div className="setting-info">
                                                        <label className="setting-label">Export Interval (minutes)</label>
                                                        <p className="setting-description">
                                                            How often to automatically export data
                                                        </p>
                                                    </div>
                                                    <div className="setting-control">
                                                        <select
                                                            value={settings.sync_interval}
                                                            onChange={(e) => handleSettingChange('sync_interval', parseInt(e.target.value))}
                                                        >
                                                            <option value={15}>15 minutes</option>
                                                            <option value={30}>30 minutes</option>
                                                            <option value={60}>1 hour</option>
                                                            <option value={120}>2 hours</option>
                                                            <option value={360}>6 hours</option>
                                                            <option value={720}>12 hours</option>
                                                            <option value={1440}>24 hours</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Quick Actions */}
                        {activeTab === 'quick-actions' && (
                            <div className="tab-panel">
                                <QuickActions />
                            </div>
                        )}

                        {/* AI Integration */}
                        {activeTab === 'ai' && (
                            <div className="tab-panel">
                                <AISettings />
                            </div>
                        )}


                        {/* WooCommerce Settings */}
                        {activeTab === 'woocommerce' && (
                            <div className="tab-panel">
                                <div className="card">
                                    <div className="card-header">
                                        <h3>WooCommerce Analytics & Tracking</h3>
                                        <p>Configure WooCommerce analytics dashboard and customer tracking features</p>
                                    </div>
                                    <div className="card-content">
                                        <div className="settings-group">
                                            <div className="setting-item">
                                                <div className="setting-info">
                                                    <label className="setting-label">WooCommerce Analytics Dashboard</label>
                                                    <p className="setting-description">
                                                        Enable comprehensive analytics dashboard with order trends, revenue analytics, customer insights, and product performance metrics
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <label className={`switch`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={settings.wooanalytics}
                                                            onChange={(e) => handlePremiumSettingChange('wooanalytics', e.target.checked)}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>

                                                </div>
                                            </div>

                                            {settings.wooanalytics && (
                                                <div className="analytics-preview">
                                                    <div className="preview-info">
                                                        <h4>📊 Analytics Dashboard Features</h4>
                                                        <div className="features-grid">
                                                            <div className="feature-item">
                                                                <span className="feature-icon">📈</span>
                                                                <div className="feature-content">
                                                                    <strong>Order Analytics</strong>
                                                                    <p>Daily, weekly, and monthly order tracking with status breakdowns</p>
                                                                </div>
                                                            </div>
                                                            <div className="feature-item">
                                                                <span className="feature-icon">💰</span>
                                                                <div className="feature-content">
                                                                    <strong>Revenue Analytics</strong>
                                                                    <p>Revenue trends, growth indicators, and performance metrics</p>
                                                                </div>
                                                            </div>
                                                            <div className="feature-item">
                                                                <span className="feature-icon">👥</span>
                                                                <div className="feature-content">
                                                                    <strong>Customer Analytics</strong>
                                                                    <p>New vs returning customers, acquisition patterns</p>
                                                                </div>
                                                            </div>
                                                            <div className="feature-item">
                                                                <span className="feature-icon">🏆</span>
                                                                <div className="feature-content">
                                                                    <strong>Top Products</strong>
                                                                    <p>Best-selling products with detailed performance data</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="preview-actions">
                                                            <button
                                                                className="btn btn-primary"
                                                                onClick={() => window.location.hash = '#/woo-analytics'}
                                                            >
                                                                🚀 View Analytics Dashboard
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="setting-item">
                                                <div className="setting-info">
                                                    <label className="setting-label">Incomplete Orders Tracking</label>
                                                    <p className="setting-description">
                                                        Track customers who start checkout but don't complete their purchase. Helps identify cart abandonment patterns and recovery opportunities.
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <label className={`switch ${!isPro() ? 'premium-disabled' : ''}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={settings.incomplete_orders_tracking}
                                                            onChange={(e) => handlePremiumSettingChange('incomplete_orders_tracking', e.target.checked)}
                                                            title={!isPro() ? 'Premium Feature - Upgrade to unlock' : ''}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                    {!isPro() && <span className="premium-badge">🔒 PRO</span>}
                                                </div>
                                            </div>

                                            <div className="setting-item">
                                                <div className="setting-info">
                                                    <label className="setting-label">Cart Interactions Tracking</label>
                                                    <p className="setting-description">
                                                        Monitor how customers interact with their cart (quantity changes, product removals, etc.). Provides insights into customer behavior and product performance.
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <label className={`switch ${!isPro() ? 'premium-disabled' : ''}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={settings.cart_interactions_tracking}
                                                            onChange={(e) => handlePremiumSettingChange('cart_interactions_tracking', e.target.checked)}
                                                            title={!isPro() ? 'Premium Feature - Upgrade to unlock' : ''}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                    {!isPro() && <span className="premium-badge">🔒 PRO</span>}
                                                </div>
                                            </div>

                                            {(settings.incomplete_orders_tracking || settings.cart_interactions_tracking) && (
                                                <>
                                                    <div className="setting-item">
                                                        <div className="setting-info">
                                                            <label className="setting-label">Abandonment Delay (minutes)</label>
                                                            <p className="setting-description">
                                                                How long to wait before marking a checkout session as abandoned
                                                            </p>
                                                        </div>
                                                        <div className="setting-control">
                                                            <select
                                                                value={settings.abandonment_delay_minutes}
                                                                onChange={(e) => handleSettingChange('abandonment_delay_minutes', parseInt(e.target.value))}
                                                            >
                                                                <option value={2}>2 minutes</option>
                                                                <option value={5}>5 minutes</option>
                                                                <option value={10}>10 minutes</option>
                                                                <option value={15}>15 minutes</option>
                                                                <option value={30}>30 minutes</option>
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div className="setting-item">
                                                        <div className="setting-info">
                                                            <label className="setting-label">Cleanup Old Records (days)</label>
                                                            <p className="setting-description">
                                                                Automatically delete incomplete order records older than this many days
                                                            </p>
                                                        </div>
                                                        <div className="setting-control">
                                                            <select
                                                                value={settings.cleanup_incomplete_orders_days}
                                                                onChange={(e) => handleSettingChange('cleanup_incomplete_orders_days', parseInt(e.target.value))}
                                                            >
                                                                <option value={7}>7 days</option>
                                                                <option value={14}>14 days</option>
                                                                <option value={30}>30 days</option>
                                                                <option value={60}>60 days</option>
                                                                <option value={90}>90 days</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Analytics Date Range Card */}
                                <div className="card">
                                    <div className="card-header">
                                        <h3>Analytics Date Range</h3>
                                        <p>Set the default time period shown in the WooCommerce Analytics dashboard</p>
                                    </div>
                                    <div className="card-content">
                                        <div className="settings-group">
                                            <div className="setting-item">
                                                <div className="setting-info">
                                                    <label className="setting-label">Default Date Range</label>
                                                    <p className="setting-description">
                                                        Analytics will show data for this period by default.
                                                        {/* {!isPro() && <span style={{ color: '#6366f1', marginLeft: 6 }}>🔒 Extended ranges require PRO. Free plan shows Today only.</span>} */}
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <select
                                                        value={settings.analytics_date_range || 'today'}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            if (val !== 'today' && !isPro()) {
                                                                showPremiumModal();
                                                                return;
                                                            }
                                                            handleSettingChange('analytics_date_range', val);
                                                        }}
                                                    >
                                                        <option value="today">Today</option>
                                                        <option value="week" disabled={!isPro()}>Last 7 Days {!isPro() ? '🔒' : ''}</option>
                                                        <option value="fifteen_days" disabled={!isPro()}>Last 15 Days {!isPro() ? '🔒' : ''}</option>
                                                        <option value="month" disabled={!isPro()}>Last 30 Days {!isPro() ? '🔒' : ''}</option>
                                                        <option value="year" disabled={!isPro()}>Last 12 Months {!isPro() ? '🔒' : ''}</option>
                                                        <option value="all" disabled={!isPro()}>All Time {!isPro() ? '🔒' : ''}</option>
                                                        <option value="custom" disabled={!isPro()}>Custom Range {!isPro() ? '🔒' : ''}</option>
                                                    </select>
                                                    {!isPro() && <span className="premium-badge">🔒 PRO</span>}
                                                </div>
                                            </div>
                                            {(settings.analytics_date_range === 'custom' && isPro()) && (
                                                <div className="setting-item">
                                                    <div className="setting-info">
                                                        <label className="setting-label">Custom Date Range</label>
                                                    </div>
                                                    <div className="setting-control" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                        <input
                                                            type="date"
                                                            value={settings.analytics_date_from || ''}
                                                            onChange={(e) => handleSettingChange('analytics_date_from', e.target.value)}
                                                            className="form-control"
                                                        />
                                                        <span>to</span>
                                                        <input
                                                            type="date"
                                                            value={settings.analytics_date_to || ''}
                                                            onChange={(e) => handleSettingChange('analytics_date_to', e.target.value)}
                                                            className="form-control"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* AI Revenue Analysis Card */}
                                <div className="card">
                                    <div className="card-header">
                                        <h3>AI Revenue Analysis</h3>
                                        <p>Let AI analyze your store data and answer business questions</p>
                                    </div>
                                    <div className="card-content">
                                        <div className="settings-group">
                                            <div className="setting-item">
                                                <div className="setting-info">
                                                    <label className="setting-label">Revenue Analysis with AI</label>
                                                    <p className="setting-description">
                                                        Adds a floating AI assistant panel on the WooCommerce Analytics page. Ask questions about your revenue, get insights, and receive actionable recommendations powered by your configured AI provider.
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <label className={`switch ${!isPro() ? 'premium-disabled' : ''}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={settings.ai_revenue_analysis || false}
                                                            onChange={(e) => handlePremiumSettingChange('ai_revenue_analysis', e.target.checked)}
                                                            title={!isPro() ? 'Premium Feature - Upgrade to unlock' : ''}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                    {!isPro() && <span className="premium-badge">🔒 PRO</span>}
                                                </div>
                                            </div>
                                            {settings.ai_revenue_analysis && isPro() && (
                                                <div className="analytics-preview">
                                                    <div className="preview-info">
                                                        <h4>🤖 AI Revenue Analysis Features</h4>
                                                        <div className="features-grid">
                                                            <div className="feature-item">
                                                                <span className="feature-icon">📊</span>
                                                                <div className="feature-content">
                                                                    <strong>Instant Analysis</strong>
                                                                    <p>AI analyzes all your revenue, orders, and product data in seconds</p>
                                                                </div>
                                                            </div>
                                                            <div className="feature-item">
                                                                <span className="feature-icon">💬</span>
                                                                <div className="feature-content">
                                                                    <strong>Ask Questions</strong>
                                                                    <p>Ask anything about your store performance and get data-driven answers</p>
                                                                </div>
                                                            </div>
                                                            <div className="feature-item">
                                                                <span className="feature-icon">🎯</span>
                                                                <div className="feature-content">
                                                                    <strong>Actionable Insights</strong>
                                                                    <p>Get specific recommendations to improve revenue and conversions</p>
                                                                </div>
                                                            </div>
                                                            <div className="feature-item">
                                                                <span className="feature-icon">🔄</span>
                                                                <div className="feature-content">
                                                                    <strong>Conversation Memory</strong>
                                                                    <p>AI remembers your previous questions for contextual follow-ups</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="preview-actions">
                                                            <button
                                                                className="btn btn-primary"
                                                                onClick={() => window.location.hash = '#/woo-analytics'}
                                                            >
                                                                🚀 Open Analytics Dashboard
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Bulk Edit Optimization Card */}
                                <div className="card">
                                    <div className="card-header">
                                        <h3>Bulk Edit Optimization</h3>
                                        <p>Configure batch processing for large bulk operations to prevent server overload</p>
                                    </div>
                                    <div className="card-content">
                                        <div className="settings-group">
                                            <div className="setting-item">
                                                <div className="setting-info">
                                                    <label className="setting-label">Optimize Mode</label>
                                                    <p className="setting-description">
                                                        Enable batch processing for bulk operations with large datasets (500K+ items).
                                                        When enabled, operations are processed in smaller chunks to prevent server timeouts and memory issues.
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <label className={`switch ${!isPro() ? 'premium-disabled' : ''}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={settings.bulk_edit_optimize_mode}
                                                            onChange={(e) => handlePremiumSettingChange('bulk_edit_optimize_mode', e.target.checked)}
                                                            title={!isPro() ? 'Premium Feature - Upgrade to unlock' : ''}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                    {!isPro() && <span className="premium-badge">🔒 PRO</span>}
                                                </div>
                                            </div>

                                            {settings.bulk_edit_optimize_mode && (
                                                <div className="setting-item">
                                                    <div className="setting-info">
                                                        <label className="setting-label">Batch Size</label>
                                                        <p className="setting-description">
                                                            Number of items to process in each batch. Lower values reduce server load but take longer.
                                                            Recommended: 5000 for most servers.
                                                        </p>
                                                    </div>
                                                    <div className="setting-control">
                                                        <div className="input-group">
                                                            <input
                                                                type="number"
                                                                min="100"
                                                                max="50000"
                                                                step="100"
                                                                value={settings.bulk_edit_batch_size}
                                                                onChange={(e) => handleSettingChange('bulk_edit_batch_size', parseInt(e.target.value) || 5000)}
                                                                className="form-control"
                                                            />
                                                            <span className="input-group-text">items</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {settings.bulk_edit_optimize_mode && (
                                                <div className="setting-item">
                                                    <div className="setting-info">
                                                        <label className="setting-label">Activation Threshold</label>
                                                        <p className="setting-description">
                                                            Minimum number of items required to trigger batch processing.
                                                            Below this threshold, regular processing is used. Recommended: 1000 items.
                                                        </p>
                                                    </div>
                                                    <div className="setting-control">
                                                        <div className="input-group">
                                                            <input
                                                                type="number"
                                                                min="10"
                                                                max="10000"
                                                                step="10"
                                                                value={settings.bulk_edit_threshold}
                                                                onChange={(e) => handleSettingChange('bulk_edit_threshold', parseInt(e.target.value) || 1000)}
                                                                className="form-control"
                                                            />
                                                            <span className="input-group-text">items</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {settings.bulk_edit_optimize_mode && (
                                                <div className="optimization-preview">
                                                    <div className="preview-info">
                                                        <h4>🚀 Optimization Features</h4>
                                                        <div className="features-grid">
                                                            <div className="feature-item">
                                                                <span className="feature-icon">⚡</span>
                                                                <div className="feature-content">
                                                                    <strong>Batch Processing</strong>
                                                                    <p>Process {settings.bulk_edit_batch_size.toLocaleString()} items at a time to prevent timeouts</p>
                                                                </div>
                                                            </div>
                                                            <div className="feature-item">
                                                                <span className="feature-icon">🎯</span>
                                                                <div className="feature-content">
                                                                    <strong>Smart Activation</strong>
                                                                    <p>Automatically activates when processing {settings.bulk_edit_threshold.toLocaleString()}+ items</p>
                                                                </div>
                                                            </div>
                                                            <div className="feature-item">
                                                                <span className="feature-icon">📊</span>
                                                                <div className="feature-content">
                                                                    <strong>Progress Tracking</strong>
                                                                    <p>Real-time progress bar with completion percentage</p>
                                                                </div>
                                                            </div>
                                                            <div className="feature-item">
                                                                <span className="feature-icon">🛡️</span>
                                                                <div className="feature-content">
                                                                    <strong>Server Protection</strong>
                                                                    <p>Prevents memory exhaustion and server crashes</p>
                                                                </div>
                                                            </div>
                                                            <div className="feature-item">
                                                                <span className="feature-icon">♾️</span>
                                                                <div className="feature-content">
                                                                    <strong>Unlimited Scale</strong>
                                                                    <p>Handle millions of products/orders without limits</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="optimization-note">
                                                            <div className="note-icon">💡</div>
                                                            <div className="note-content">
                                                                <strong>When to use:</strong> Enable this when bulk editing more than {settings.bulk_edit_threshold.toLocaleString()} items or if you experience timeouts during bulk operations.
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* WooCommerce Status Card */}
                                <div className="card">
                                    <div className="card-header">
                                        <h3>WooCommerce Integration Status</h3>
                                    </div>
                                    <div className="card-content">
                                        <div className="woo-status-check">
                                            <div className="status-item">
                                                <span className="status-icon">🛒</span>
                                                <div className="status-content">
                                                    <strong>WooCommerce Plugin</strong>
                                                    <p id="woo-status">Checking...</p>
                                                </div>
                                            </div>
                                            <div className="status-item">
                                                <span className="status-icon">📊</span>
                                                <div className="status-content">
                                                    <strong>Analytics Tables</strong>
                                                    <p id="tables-status">Checking...</p>
                                                </div>
                                            </div>
                                            <div className="status-item">
                                                <span className="status-icon">🔧</span>
                                                <div className="status-content">
                                                    <strong>Tracking Scripts</strong>
                                                    <p id="scripts-status">Checking...</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="status-actions">
                                            <button
                                                className={`btn btn-secondary ${!isPro() ? 'premium-disabled' : ''}`}
                                                onClick={() => {
                                                    if (!isPro()) {
                                                        showPremiumModal();
                                                        return;
                                                    }

                                                    // Check WooCommerce status
                                                    document.getElementById('woo-status').textContent = 'Checking...';
                                                    document.getElementById('tables-status').textContent = 'Checking...';
                                                    document.getElementById('scripts-status').textContent = 'Checking...';

                                                    // Simulate status check
                                                    setTimeout(() => {
                                                        document.getElementById('woo-status').textContent = 'Active ✅';
                                                        document.getElementById('tables-status').textContent = 'Ready ✅';
                                                        document.getElementById('scripts-status').textContent = 'Loaded ✅';
                                                    }, 1000);
                                                }}
                                                title={!isPro() ? 'Premium Feature - Upgrade to unlock' : 'Check WooCommerce integration status'}
                                            >
                                                🔄 Check Status {!isPro() ? '🔒' : ''}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Sync Settings */}
                        {activeTab === 'sync' && (
                            <div className="tab-panel">
                                <div className="card">
                                    <div className="card-header">
                                        <h3>Export Configuration</h3>
                                        <p>Configure what data to sync and how</p>
                                    </div>
                                    <div className="card-content">
                                        <div className="settings-group">
                                            <div className="setting-item">
                                                <div className="setting-info">
                                                    <label className="setting-label">Export Orders</label>
                                                    <p className="setting-description">
                                                        Include orders in synchronization
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <label className="switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={settings.sync_orders}
                                                            onChange={(e) => handleSettingChange('sync_orders', e.target.checked)}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="setting-item">
                                                <div className="setting-info">
                                                    <label className="setting-label">Export Products</label>
                                                    <p className="setting-description">
                                                        Include products in synchronization
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <label className="switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={settings.sync_products}
                                                            onChange={(e) => handleSettingChange('sync_products', e.target.checked)}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="setting-item">
                                                <div className="setting-info">
                                                    <label className="setting-label">Export Customers</label>
                                                    <p className="setting-description">
                                                        Include customers in synchronization
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <label className="switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={settings.sync_customers}
                                                            onChange={(e) => handleSettingChange('sync_customers', e.target.checked)}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="setting-item">
                                                <div className="setting-info">
                                                    <label className="setting-label">Batch Size</label>
                                                    <p className="setting-description">
                                                        Number of records to process in each batch
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <select
                                                        value={settings.batch_size}
                                                        onChange={(e) => handleSettingChange('batch_size', parseInt(e.target.value))}
                                                    >
                                                        <option value={50}>50 records</option>
                                                        <option value={100}>100 records</option>
                                                        <option value={200}>200 records</option>
                                                        <option value={500}>500 records</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        )}

                        {/* Notifications Settings */}
                        {activeTab === 'notifications' && (
                            <div className="tab-panel">
                                <div className="card upcoming-feature">
                                    <div className="card-header">
                                        <h3>
                                            Notification Settings
                                            <span className="upcoming-badge">Coming Soon</span>
                                        </h3>
                                        <p>Configure email notifications and alerts</p>
                                    </div>
                                    <div className="card-content">
                                        <div className="settings-group">
                                            <div className="setting-item">
                                                <div className="setting-info">
                                                    <label className="setting-label">Email Notifications</label>
                                                    <p className="setting-description">
                                                        Enable email notifications for sync events
                                                    </p>
                                                </div>
                                                <div className="setting-control">
                                                    <label className="switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={settings.email_notifications}
                                                            onChange={(e) => handleSettingChange('email_notifications', e.target.checked)}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                </div>
                                            </div>

                                            {settings.email_notifications && (
                                                <>
                                                    <div className="form-group">
                                                        <label>Notification Email</label>
                                                        <input
                                                            type="email"
                                                            value={settings.notification_email}
                                                            onChange={(e) => handleSettingChange('notification_email', e.target.value)}
                                                            placeholder="Enter email address for notifications"
                                                        />
                                                    </div>

                                                    <div className="setting-item">
                                                        <div className="setting-info">
                                                            <label className="setting-label">Sync Success Notifications</label>
                                                            <p className="setting-description">
                                                                Get notified when sync completes successfully
                                                            </p>
                                                        </div>
                                                        <div className="setting-control">
                                                            <label className="switch">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={settings.sync_success_notifications}
                                                                    onChange={(e) => handleSettingChange('sync_success_notifications', e.target.checked)}
                                                                />
                                                                <span className="slider"></span>
                                                            </label>
                                                        </div>
                                                    </div>

                                                    <div className="setting-item">
                                                        <div className="setting-info">
                                                            <label className="setting-label">Sync Error Notifications</label>
                                                            <p className="setting-description">
                                                                Get notified when sync encounters errors
                                                            </p>
                                                        </div>
                                                        <div className="setting-control">
                                                            <label className="switch">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={settings.sync_error_notifications}
                                                                    onChange={(e) => handleSettingChange('sync_error_notifications', e.target.checked)}
                                                                />
                                                                <span className="slider"></span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* More Tab */}
                        {activeTab === 'more' && (
                            <div className="tab-panel">
                                <div className="plugins-grid">
                                    {/* Product Display Plugin */}
                                    <div className="card plugin-card">
                                        <div className="plugin-header">
                                            <div className="plugin-icon">
                                                <img
                                                    src="https://ps.w.org/product-display/assets/icon-128x128.png?rev=3390638"
                                                    alt="Product Display"
                                                />
                                            </div>
                                            <div className="plugin-title">
                                                <h3>Product Display for WooCommerce</h3>
                                                <p className="plugin-tagline">Enhance your product showcase</p>
                                            </div>
                                        </div>
                                        <div className="card-content">
                                            <p className="plugin-description">
                                                Create stunning WooCommerce Product layouts and interactive Product showcases using Grid, Slider, List, Card, Gallery, Table, and more.
                                            </p>
                                            <div className="plugin-features">
                                                <span className="feature-badge">✨ Multiple Layouts</span>
                                                <span className="feature-badge">📱 Responsive</span>
                                                <span className="feature-badge">🎨 Customizable</span>
                                            </div>
                                            <button
                                                className="btn btn-primary btn-block"
                                                onClick={() => {
                                                    window.location.href = '/wp-admin/plugin-install.php?s=product%20display%20wpazleen&tab=search&type=term';
                                                }}
                                            >
                                                Install Now →
                                            </button>
                                            <p className="plugin-author">⭐ Free by WPAzleen</p>
                                        </div>
                                    </div>

                                    {/* Activity Guard Plugin */}
                                    <div className="card plugin-card">
                                        <div className="plugin-header">
                                            <div className="plugin-icon">
                                                <img
                                                    src="https://ps.w.org/notifier-to-slack/assets/icon.svg?rev=3388086"
                                                    alt="Activity Guard"
                                                />
                                            </div>
                                            <div className="plugin-title">
                                                <h3>Activity Guard</h3>
                                                <p className="plugin-tagline">Complete Security & Activity Log</p>
                                            </div>
                                        </div>
                                        <div className="card-content">
                                            <p className="plugin-description">
                                                Track everything on your WordPress site. Monitor user behavior, admin changes, WooCommerce events + analytics, with instant notifier support.
                                            </p>
                                            <div className="plugin-features">
                                                <span className="feature-badge">🛡️ Security</span>
                                                <span className="feature-badge">📊 Analytics</span>
                                                <span className="feature-badge">🔔 Notifications</span>
                                            </div>
                                            <button
                                                className="btn btn-primary btn-block"
                                                onClick={() => {
                                                    window.location.href = '/wp-admin/plugin-install.php?s=activity%20guard%20wpazleen&tab=search&type=term';
                                                }}
                                            >
                                                Install Now →
                                            </button>
                                            <p className="plugin-author">⭐ Free by WPAzleen</p>
                                        </div>
                                    </div>

                                    {/* AskAny Plugin */}
                                    <div className="card plugin-card">
                                        <div className="plugin-header">
                                            <div className="plugin-icon">
                                                <img
                                                    src="https://ps.w.org/askany/assets/icon-128x128.png?rev=3294851"
                                                    alt="AskAny"
                                                />
                                            </div>
                                            <div className="plugin-title">
                                                <h3>AskAny</h3>
                                                <p className="plugin-tagline">AI-Powered Chat Assistant</p>
                                            </div>
                                        </div>
                                        <div className="card-content">
                                            <p className="plugin-description">
                                                Advanced AI chatbot featuring SSE technology, powered by OpenAI, DeepSeek, and Gemini. Delivers instant responses for customer support.
                                            </p>
                                            <div className="plugin-features">
                                                <span className="feature-badge">🤖 AI Powered</span>
                                                <span className="feature-badge">⚡ Instant</span>
                                                <span className="feature-badge">💬 Chat Support</span>
                                            </div>
                                            <button
                                                className="btn btn-primary btn-block"
                                                onClick={() => {
                                                    window.location.href = '/wp-admin/plugin-install.php?s=askany%20wpazleen&tab=search&type=term';
                                                }}
                                            >
                                                Install Now →
                                            </button>
                                            <p className="plugin-author">⭐ Free by WPAzleen</p>
                                        </div>
                                    </div>

                                    {/* Simple Form Plugin */}
                                    <div className="card plugin-card">
                                        <div className="plugin-header">
                                            <div className="plugin-icon">
                                                <img
                                                    src="https://ps.w.org/simple-form/assets/icon-128x128.png?rev=3399184"
                                                    alt="Simple Form"
                                                />
                                            </div>
                                            <div className="plugin-title">
                                                <h3>Simple Form</h3>
                                                <p className="plugin-tagline">Ultimate Form Builder</p>
                                            </div>
                                        </div>
                                        <div className="card-content">
                                            <p className="plugin-description">
                                                Comprehensive form builder with Quiz, Poll, Multi Step Forms, Conditional Logic, Form Analytics & Advanced Integrations. No coding required!
                                            </p>
                                            <div className="plugin-features">
                                                <span className="feature-badge">📝 Drag & Drop</span>
                                                <span className="feature-badge">📊 Analytics</span>
                                                <span className="feature-badge">🔗 Integrations</span>
                                            </div>
                                            <button
                                                className="btn btn-primary btn-block"
                                                onClick={() => {
                                                    window.location.href = '/wp-admin/plugin-install.php?s=simple%20form%20wpazleen&tab=search&type=term';
                                                }}
                                            >
                                                Install Now →
                                            </button>
                                            <p className="plugin-author">⭐ Free by WPAzleen</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Premium Modal */}
            <PriceModal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                onDecline={closeModal}
                type={modalConfig.type}
                title={modalConfig.title}
                message={modalConfig.message}
                confirmText={modalConfig.confirmText}
                declineText={modalConfig.declineText}
                position={modalConfig.position}
            />

        </div>
    );
};

export default Settings;